import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PrayerForm from "@/components/PrayerForm";
import SEOHead from "@/components/SEOHead";
import QuestionFormModal from "@/components/QuestionFormModal";

const BecomingIntensive = () => {
  const [questionFormOpen, setQuestionFormOpen] = useState(false);

  const sixBs = [
    {
      num: "01",
      title: "Beliefs",
      desc: "Identify the underlying mindsets driving performance and perfectionism.",
    },
    {
      num: "02",
      title: "Belonging",
      desc: "Examine key relationships and overcome isolated survival mode.",
    },
    {
      num: "03",
      title: "Boundaries",
      desc: "Establish healthy limits without guilt to protect your capacity.",
    },
    {
      num: "04",
      title: "Business",
      desc: "Align work, ministry, and calling with sustainable rhythms.",
    },
    {
      num: "05",
      title: "Budgeting",
      desc: "Build financial peace and intentional stewardship with resources.",
    },
    {
      num: "06",
      title: "Body",
      desc: "Listen to physical signals of stress and honor physical stewardship.",
    },
  ];

  return (
    <>
      <SEOHead
        title="The Becoming Intensive: 6-Week Scripture-Rooted Coaching Experience"
        description="A six-week guided experience with Scripture-rooted teaching, live coaching, and the six B's framework. Find the source of your burnout and build a plan forward."
        canonical="https://iamieshiadanielle.com/programs/becoming-intensive"
        ogImage="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a946184a90361de8b517bcb.jpg"
        schemaJson={[
          {
            "@type": "WebPage",
            "@id":
              "https://iamieshiadanielle.com/programs/becoming-intensive#webpage",
            url: "https://iamieshiadanielle.com/programs/becoming-intensive",
            name: "The Becoming Intensive: 6-Week Scripture-Rooted Coaching Experience",
            isPartOf: { "@id": "https://iamieshiadanielle.com/#website" },
            about: { "@id": "https://iamieshiadanielle.com/#person" },
            inLanguage: "en-US",
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://iamieshiadanielle.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Programs",
                item: "https://iamieshiadanielle.com/programs",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Becoming Intensive",
                item: "https://iamieshiadanielle.com/programs/becoming-intensive",
              },
            ],
          },
          {
            "@type": "Course",
            "@id":
              "https://iamieshiadanielle.com/programs/becoming-intensive#course",
            name: "Becoming Intensive",
            description:
              "A six-week guided experience with Scripture-rooted teaching, live coaching and guided reflection, community and accountability, and a clear plan across the six B's framework, including the RESET process for decision-making.",
            provider: { "@id": "https://iamieshiadanielle.com/#organization" },
            instructor: { "@id": "https://iamieshiadanielle.com/#person" },
            url: "https://iamieshiadanielle.com/programs/becoming-intensive",
            offers: {
              "@type": "Offer",
              price: "997",
              priceCurrency: "USD",
              availability: "https://schema.org/InStock",
              url: "https://iamieshiadanielle.com/programs/becoming-intensive",
            },
            hasCourseInstance: {
              "@type": "CourseInstance",
              courseMode: "Online",
              startDate: "2026-09-22",
              courseSchedule: {
                "@type": "Schedule",
                repeatCount: 6,
                repeatFrequency: "Weekly",
              },
            },
          },
        ]}
      />

      <div className="min-h-screen bg-[#FAF6F0] text-[#380C1E] flex flex-col font-sans">
        <Header />

        <main className="flex-1">
          {/* Hero */}
          <section className="bg-[#FBF1F4] py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-6 space-y-5">
                <p className="text-xs font-semibold text-[#380C1E]/60 uppercase tracking-widest">
                  Six-week guided experience
                </p>
                <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#380C1E] leading-tight">
                  You can be grateful for your life and still know something has
                  to change.
                </h1>
                <p className="text-base text-[#380C1E]/70 font-light leading-relaxed max-w-md">
                  The Becoming Intensive helps you find what has been draining
                  you, interrupt the cycle, and build a practical plan for the
                  woman God is calling you to become.
                </p>

                <div className="bg-white rounded-2xl border border-[#E8DEC8] px-6 py-4 flex flex-wrap items-center gap-x-6 gap-y-2 max-w-lg shadow-sm">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="font-serif text-2xl font-bold text-[#380C1E]">
                      $997
                    </span>
                  </div>
                  <div className="w-full h-px bg-[#E8DEC8] sm:hidden" />
                  <span className="text-[11px] uppercase font-medium tracking-wider text-[#380C1E]/60">
                    Next Intensive begins January 11th, 2027 · Six weeks · Live
                    coaching · Community
                  </span>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  <a
                    href="https://store.iamieshiadanielle.com/product-details/product/6a99c533a3a2579f28b90d53"
                    className="bg-[#9E1B50] text-white hover:bg-[#801340] font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full shadow-sm transition-all"
                  >
                    Join the Becoming Intensive →
                  </a>
                  <a
                    href="https://quiz.iamieshiadanielle.com/quiz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="border border-[#4A0E26] text-[#4A0E26] hover:bg-[#4A0E26] hover:text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full transition-all"
                  >
                    Take The Quiz →
                  </a>
                  <button
                    onClick={() => setQuestionFormOpen(true)}
                    className="border border-[#4A0E26] text-[#4A0E26] hover:bg-[#4A0E26] hover:text-white font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full transition-all"
                  >
                    Click Here If You Have Questions
                  </button>
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="relative rounded-[2rem] overflow-hidden bg-neutral-200 border border-[#E8DEC8] shadow-lg aspect-video">
                  <video
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a97954007f362e3213b96ba.mp4#t=0.1"
                    controls
                    playsInline
                    preload="metadata"
                    aria-label="Ieshia Danielle leading the Becoming Intensive"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Truth Strip */}
          <section className="text-white py-12 px-6 bg-accent">
            <div className="max-w-5xl mx-auto text-center">
              <p className="font-serif text-2xl md:text-3xl font-normal italic tracking-tight leading-snug">
                You are strong. But strength has become the reason nobody
                notices you are disappearing.
              </p>
            </div>
          </section>

          {/* Pain Point Recognition */}
          <section id="quiz" className="bg-white py-16 md:py-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div className="space-y-3 lg:sticky lg:top-24">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  Let us tell the truth
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E] leading-tight">
                  You are not tired because you cannot handle your life.
                </h2>
              </div>

              <div className="space-y-4">
                {[
                  "You wake up already thinking about what everyone else needs.",
                  "You keep saying yes because disappointing people feels heavier than disappointing yourself.",
                  "You have ideas, goals, and a calling, but no room to hear your own thoughts.",
                  "You keep waiting for life to slow down before you finally deal with you.",
                ].map((point) => (
                  <div
                    key={point}
                    className="bg-[#EFE6D2] rounded-2xl p-6 text-[#380C1E]/80 text-base font-light leading-relaxed"
                  >
                    {point}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* The Cost of Staying the Same */}
          <section className="bg-[#4A0E26] py-16 md:py-24">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
              <div className="space-y-3">
                <p className="text-xs font-semibold text-white/70 uppercase tracking-widest">
                  The cost of staying the same
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-white max-w-xl">
                  Staying here is already costing you.
                </h2>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  "Your peace",
                  "Your presence",
                  "Your health",
                  "Your vision",
                  "Your relationships",
                  "Your self-trust",
                ].map((cost) => (
                  <div
                    key={cost}
                    className="bg-white/10 rounded-2xl p-6 flex items-center"
                  >
                    <span className="font-serif italic text-lg font-normal text-white">
                      {cost}
                    </span>
                  </div>
                ))}
              </div>

              <p className="text-white/80 text-base leading-relaxed max-w-2xl">
                Stop asking only what it will cost to change. Start asking what
                staying the same is already costing you.
              </p>
            </div>
          </section>

          {/* The Six B's */}
          <section className="bg-white py-16 md:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
              <div className="max-w-3xl space-y-3">
                <p className="text-xs font-semibold text-[#9E1B50] uppercase tracking-widest italic">
                  The complete pattern
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E]">
                  Burnout has a source. We are going to find yours.
                </h2>
                <p className="text-[#380C1E]/70 font-light">
                  The six B's give us a practical way to see the beliefs,
                  relationships, responsibilities, health, work, and money
                  patterns shaping your life.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sixBs.map((b) => (
                  <div
                    key={b.num}
                    className="bg-[#EFE6D2] rounded-2xl p-8 space-y-8"
                  >
                    <span className="text-xs font-semibold text-[#380C1E]/50 tracking-wider">
                      {b.num}
                    </span>
                    <h3 className="font-serif text-2xl font-normal text-[#380C1E]">
                      {b.title}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Inside the Intensive */}
          <section className="bg-[#9E1B50] py-16 md:py-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-5">
                <div className="rounded-[2rem] overflow-hidden bg-white/10 border border-white/20 shadow-lg aspect-[4/6]">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a9468520914f112152008bf.jpg"
                    alt="Live coaching session inside the Becoming Intensive"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>

              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-white/80">
                  Inside the Intensive
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-white leading-tight">
                  Truth becomes practical action.
                </h2>
                <ul className="space-y-3 text-white/90">
                  {[
                    "Six weeks of Scripture-rooted teaching",
                    "Live coaching and guided reflection",
                    "A clear plan across your six B's",
                    "Community, structure, and accountability",
                    "The RESET process for decisions that last",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="text-white font-bold">•</span>
                      <span className="text-base font-light">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* You on the Other Side */}
          <section className="bg-white py-16 md:py-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7 space-y-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-[#380C1E]/70">
                  You on the other side
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-[#380C1E] leading-tight">
                  Clear. Present. Decisive. No longer waiting for permission to
                  steward your own life.
                </h2>
                <p className="text-[#380C1E]/70 text-base leading-relaxed font-light max-w-xl">
                  You will leave knowing what has been draining you, how to
                  interrupt the pattern, and what practical steps belong to this
                  season.
                </p>
                <p className="font-serif text-[#380C1E] font-normal italic border-t border-[#E8DEC8] pt-6 max-w-xl">
                  "The woman you are becoming needs a decision from the woman
                  you are today."
                </p>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-[2rem] overflow-hidden bg-neutral-200 border border-[#E8DEC8] shadow-lg aspect-[4/6]">
                  <img
                    src="https://assets.cdn.filesafe.space/N5B75kjUT85NEZBh2RaZ/media/6a94e8790914f112152aafda.jpg"
                    alt="Ieshia Danielle on the other side of becoming"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Enrollment Call to Action */}
          <section id="enroll" className="bg-[#4A0E26] py-16 md:py-24">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7 space-y-4">
                <p className="text-xs font-semibold text-[#F2A6C4] uppercase tracking-widest">
                  Enrollment
                </p>
                <h2 className="font-serif text-3xl md:text-5xl font-normal text-white leading-tight">
                  The woman you are becoming needs a decision from the woman you
                  are today.
                </h2>
              </div>

              <div className="lg:col-span-5 space-y-4">
                <p className="font-serif text-5xl md:text-6xl font-bold text-white">
                  $997
                </p>
                <p className="text-white/70 text-sm leading-relaxed">
                  Six-week Becoming Intensive
                  <br />
                  Kickoff: September 22, 2026
                </p>
                <p className="text-white/60 text-xs font-light">
                  Licensed for personal use only. Full license:{" "}
                  <Link
                    to="/product-license"
                    className="underline hover:text-white"
                  >
                    iamieshiadanielle.com/product-license
                  </Link>
                </p>
                <button className="bg-white hover:bg-[#F5E9D7] text-[#380C1E] font-bold uppercase text-[11px] tracking-wider px-8 py-4 rounded-full transition-all shadow-sm inline-flex items-center gap-2">
                  Reserve your place →
                </button>
              </div>
            </div>
          </section>

          <PrayerForm />
        </main>

        <Footer />
        <QuestionFormModal
          open={questionFormOpen}
          onClose={() => setQuestionFormOpen(false)}
        />
      </div>
    </>
  );
};

export default BecomingIntensive;
